import ConnectedContainer from "components/container/ConnectedContainer.tsx";
import { useAppDispatch, useAppSelector } from "config/store";
import { UserType } from "globals/types/models.ts";
import { authActions } from "config/store/modules/auth.ts";
import { tryAsyncFunc } from "globals/functions/async.ts";
import ajax from "config/ajax.ts";
import LoadingContainer from "components/container/LoadingContainer.tsx";
import { ErrorMessage, Form, Formik } from "formik";
import Button from "components/atom/button/Button.tsx";
import { useTranslation } from "react-i18next";
import { getValuesFromUser, UserFormValuesType } from "utils/form.ts";
import UserForm from "components/layouts/form/UserForm.tsx";
import Icon from "components/atom/Icon.tsx";
import FormLabel from "components/form/FormLabel.tsx";
import { Elements } from "@stripe/react-stripe-js";
import PaymentMethodForm from "components/layouts/form/PaymentMethodForm.tsx";
import { Fragment, useMemo, useRef } from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useMount } from "react-use";
import InvoiceCard from "components/layouts/card/InvoiceCard.tsx";
import { useNavigate } from "react-router-dom";
import { USER_PLAN_TYPES, USER_TYPES } from "globals/constants.ts";
import dayjs from "dayjs";
import { getUserNotCompletedFields } from "utils/data/user.ts";

const getStripePromise = () => loadStripe(process.env.STRIPE_PUBLIC_KEY as string);


function MeAccountPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentUser: UserType | null = useAppSelector(state => state.auth.user);
  const currentUserIsCompleted: boolean = useAppSelector(state => state.auth.userIsCompleted);

  const navigate = useNavigate();
  const currentLang = useAppSelector(state => state.global.currentLang);
  const subscription = useAppSelector(state => state.auth.subscription);
  const invoices = useAppSelector(state => state.auth.invoices);
  const stripePromise = useRef<Stripe | null>(null);


  useMount(async () => {
    dispatch(authActions.getMyInvoices());
    dispatch(authActions.getMySubscription());
    stripePromise.current = await getStripePromise();
  });


  const notCompletedFields = useMemo(() => {
    if (!currentUser) return [];
    return getUserNotCompletedFields(currentUser);
  }, [currentUser]);

  if (!currentUser) {
    return null;
  }

  return (
    <ConnectedContainer title={t("user.account")}>
      <div className="mx-auto w-full md:max-w-[506px]">
        {!currentUserIsCompleted && (
          <div
            className="mb-6 rounded-md bg-red-500 px-4 py-2">
            <div className="flex flex-row justify-start items-center gap-4">
              <Icon name="alert" className="text-white w-8 h-8 min-w-8 block" />
              <p className="text-white">
                {t("user.complete")}
              </p>
            </div>
            {notCompletedFields.length > 0 && (
              <div className="pl-12">
                <p className="text-white text-xs font-semibold">
                  {t("forms.errors.fieldsMissing")}:
                </p>
                <ul className="space-y-1">
                  {notCompletedFields.map(field => (
                    <li className="text-white text-xs" key={field}>
                      {t(`forms.${field}`)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <Formik
          initialValues={getValuesFromUser(currentUser)}
          validateOnChange={false}
          validateOnBlur={false}
          validate={values => {
            const errors: Partial<{ [K in keyof UserFormValuesType]: string }> = {};//same keys as type but with string value
            if (!values.email) {
              errors.email = t("forms.errors.requiredEmail");
            }
            if (!values.name) {
              errors.name = t("forms.errors.requiredName");
            }
            return errors;
          }}
          onSubmit={async (values, { setSubmitting, setStatus, setFieldError, resetForm }) => {
            setSubmitting(true);
            setStatus(null);

            //sync with values
            if (values.sameAddress) {
              values.billingAddress = values.address;
              values.billingCity = values.city;
              values.billingZipcode = values.zipcode;
              values.billingState = values.state;
              values.billingCountry = values.country;
            }

            const [res, err] = await tryAsyncFunc(ajax.put("/connected/me", values));
            setSubmitting(false);

            if (err) {
              const errData = err.response.data || {};
              const errors = errData.errors || {};

              if (errors.global) {
                setStatus(errors.global);
                delete errors.global;
              }

              Object.keys(errors).forEach(errKey => {
                setFieldError(errKey, errors[errKey]);
              });
              return;
            }

            await dispatch(authActions.setUser(res.data.user));
            resetForm({
              values: getValuesFromUser(res.data.user)
            });

            //open verify email modal
            if (res.data.user.unverifiedEmail && res.data.user.type === USER_TYPES.EVENT_MANAGER && res.data.user.email === res.data.user.unverifiedEmail) {
              navigate({
                pathname: location.pathname,
                hash: "#/auth/verify-email"
              }, {
                state: {
                  email: res.data.user.unverifiedEmail
                }
              });
            }
          }}
        >
          {({ isSubmitting, status, dirty, values }) => (
            <LoadingContainer isLoading={isSubmitting} keepChildren>
              <Form>
                <UserForm />

                {status && (
                  <p className="error block">{status}</p>
                )}
                <div className="mt-4 flex w-full flex-row items-center justify-center">
                  <Button className="w-full" type="submit" disabled={isSubmitting || !dirty}>
                    {t("save")}
                  </Button>
                </div>

                {currentUser?.type === USER_TYPES.EVENT_MANAGER && (
                  <Fragment>
                    <div className="mt-12">
                      <div className="c__input cursor-pointer" onClick={() => {
                        //todo check if form is dirty, if yes dont navigate
                        navigate("/profile/me/plans");
                      }}>
                        <FormLabel>
                          {t("forms.planType")}
                        </FormLabel>
                        <div className="rounded-md border border-gray-300 p-2 mt-1 relative">
                          <input type="text"
                                 className="text-xs pointer-events-none h-full w-full"
                                 value={values.planType ? t(`plans.titles.${values.planType}`) : ""} />
                          <div
                            className="absolute right-2 top-1/2 flex -translate-y-1/2 flex-row items-center justify-end  gap-1">
                            <span className="text-xs text-gray-400">
                              {t("edit")}
                            </span>
                            <Icon name="angle-right"
                                  className="h-4 w-4 min-w-4 text-gray-400" />
                          </div>

                        </div>
                        <ErrorMessage name="planType" component="p" className="error" />
                      </div>
                      <div>
                        {(values.planType === USER_PLAN_TYPES.UNIT || values.planType === USER_PLAN_TYPES.CANCELED) && (
                          <p className="text-xs">
                            {t(`plans.explanations.${values.planType}`)}
                          </p>
                        )}
                        {values.planType === USER_PLAN_TYPES.TRIAL && values.trialEndedAt && (
                          <p className="text-xs pb-2">
                            {dayjs(values.trialEndedAt).isAfter(dayjs()) ? (
                              <Fragment>
                                {t("plans.trialEndedAtExplanation", { end: dayjs.utc(values.trialEndedAt).format("D MMMM YYYY") })}
                              </Fragment>
                            ) : (
                              <Fragment>
                                {t("plans.trialIsEnded", { end: dayjs.utc(values.trialEndedAt).format("D MMMM YYYY") })}
                              </Fragment>
                            )}

                          </p>
                        )}

                        {subscription && (
                          <p className="text-xs">
                            {subscription.trial_end ? (
                              <Fragment>
                                {values.planType === USER_PLAN_TYPES.COMPLETE_MONTHLY && (
                                  <Fragment>
                                    {t("plans.explanations.completeWithTrial", {
                                      trial_end: dayjs(subscription.trial_end).format("LL"),
                                      start: dayjs(subscription.current_period_start).format("LL"),
                                      end: dayjs(subscription.current_period_end).format("LL")
                                    })}
                                  </Fragment>
                                )}
                                {values.planType === USER_PLAN_TYPES.COMPLETE_YEARLY && (
                                  <Fragment>
                                    {t("plans.explanations.completeWithTrialYearly", {
                                      trial_end: dayjs(subscription.trial_end).format("LL"),
                                      start: dayjs(subscription.current_period_start).format("LL"),
                                      end: dayjs(subscription.current_period_end).format("LL")
                                    })}
                                  </Fragment>
                                )}
                              </Fragment>
                            ) : (
                              <Fragment>
                                {t("plans.explanations.complete", {
                                  start: dayjs(subscription.current_period_start).format("LL"),
                                  end: dayjs(subscription.current_period_end).format("LL")
                                })}
                              </Fragment>
                            )}

                          </p>
                        )}
                      </div>
                    </div>


                    <div className="mt-4" id="payment-methods">
                      <FormLabel>
                        {t("forms.paymentMethods")}
                      </FormLabel>
                      <div className="mt-1 rounded-md border border-gray-300 p-4">
                        <Elements stripe={stripePromise.current} options={{
                          currency: "cad",
                          mode: "setup",
                          locale: `${currentLang}-CA`
                        }}>
                          <PaymentMethodForm />
                        </Elements>
                      </div>
                    </div>
                    {invoices.length > 0 && (
                      <div className="mt-4">
                        <FormLabel>
                          {t("forms.invoices")}
                        </FormLabel>
                        <div className="mt-1 rounded-md border border-gray-300 p-4">
                          {invoices.length === 0 && (
                            <p className="text-xs text-gray-400">
                              {t("forms.noInvoices")}
                            </p>
                          )}

                          <div className="space-y-2">
                            {invoices.map(invoice => (
                              <InvoiceCard invoice={invoice} key={invoice.id} onDownload={() => {
                                window.open(invoice.invoice_pdf, "_blank");
                              }} />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                  </Fragment>
                )}
              </Form>
            </LoadingContainer>
          )}
        </Formik>
      </div>
    </ConnectedContainer>

  );
}

export default MeAccountPage;
